import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Checkbox, Typography, Form } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { ReactComponent as EditIcon } from "../../../assets/svg/edit_1.svg";
import { ReactComponent as SendIcon } from "../../../assets/svg/send-icon.svg";
import { WHITE, GRAY_300, GRAY_400, PRIMARY_600, ERROR_600 } from "../../../constant/colors";
import { fontWeights, sizes } from "../../../constant/styles";
import { selectUser } from "../../../store/selector";
import { createEConsentRemoteSignatureRequest } from "../../../api/api";
import { SecondaryButton, OutlinedButton } from "../../customComponent/Button";
import { formatPhoneNumber } from "../../../utils/formaters";
import { formatName } from "../../../utils/user";
import { useGetSetting } from "../../../hooks/getSetting";
import RequestConsentLinkErrorAlert from "./ErrorAlert";
import ElectronicConsentAlert from "../ElectronicConsentAlert";
import useStandardizedTracking, {
  EventNames,
  ContentTypes,
  FeatureGroups
} from "../../../utils/pendo/useStandardizedTracking";

// Consent request channel types (backend expects these exact strings)
const CONSENT_REQUEST_CHANNELS = {
  EMAIL: "email",
  PHONE: "phone"
};

const { Text } = Typography;

const WhiteSendIcon = styled(SendIcon)`
  width: 17.6px;
  height: 16.04px;
  path {
    stroke: ${WHITE};
  }
`;

const BlueEditIcon = styled(EditIcon)`
  width: 18px;
  height: 18px;
  path {
    stroke: ${PRIMARY_600};
    stroke-width: 1.5;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MessagePreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MessagePreviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const MessagePreviewTitle = styled(Text)`
  font-weight: ${fontWeights.bold};
  font-size: ${sizes.small};
  line-height: 1em;
  text-transform: uppercase;
`;

const FplCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledFplCheckbox = styled(Checkbox)`
  .ant-checkbox {
    border-radius: 4px;
    width: 16px;
    height: 16px;
  }

  .ant-checkbox-inner {
    border-radius: 4px;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${PRIMARY_600};
    border-color: ${PRIMARY_600};
    border-radius: 4px;
  }
`;

const FplCheckboxLabel = styled(Text)`
  font-weight: ${fontWeights.regular};
  font-size: ${sizes.small};
  line-height: 1.5em;
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${GRAY_300};
  width: 100%;
`;

const MessageText = styled(Text)`
  font-weight: ${fontWeights.regular};
  font-size: ${sizes.small};
  line-height: 1.5em;
`;

const RequestSection = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: 20px;
  width: 100%;
`;

const ContactDetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  width: 100%;
  flex: 1;
`;

const PatientNameLabel = styled(Text)`
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.medium};
  line-height: 1.5em;
  width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-overflow: ellipsis;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  height: 20px;
`;

const StyledCheckboxGroup = styled(Checkbox.Group)`
  .ant-checkbox {
    border-radius: 4px;
    width: 16px;
    height: 16px;
  }

  .ant-checkbox-inner {
    border-radius: 4px;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: ${PRIMARY_600};
    border-color: ${PRIMARY_600};
    border-radius: 4px;
  }

  .ant-checkbox-wrapper-disabled .ant-checkbox .ant-checkbox-inner {
    border: 2px solid ${ERROR_600} !important;
    border-radius: 4px;
  }
`;

const ContactText = styled(Text)`
  font-weight: ${fontWeights.regular};
  font-size: ${sizes.small};
  line-height: 1.5em;
  text-align: left;
  color: ${(props) => (props.$isPlaceholder ? GRAY_400 : "inherit")};
`;

const EditButton = styled.div`
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const ButtonsSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  gap: 5px;
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  border-radius: 10px;
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.medium};
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 10px;

  svg {
    color: ${WHITE} !important;
  }

  svg path {
    stroke: ${WHITE} !important;
  }

  &:disabled,
  &[disabled] {
    background-color: ${GRAY_300} !important;
    color: ${WHITE} !important;
    border-color: ${GRAY_300} !important;
    cursor: not-allowed;
    opacity: 1 !important;

    svg {
      color: ${WHITE} !important;
    }

    svg path {
      stroke: ${WHITE} !important;
    }

    &:hover {
      background-color: ${GRAY_300} !important;
      color: ${WHITE} !important;
      border-color: ${GRAY_300} !important;
    }
  }
`;

const StyledOutlinedButton = styled(OutlinedButton)`
  background: transparent;
  border: none;
  border-bottom: 1.5px solid ${PRIMARY_600};
  border-radius: 0;
  font-weight: ${fontWeights.semibold};
  font-size: ${sizes.medium};
  color: ${PRIMARY_600};
  padding: 4px 0;

  &:hover {
    background: transparent;
    border-bottom: 1.5px solid ${PRIMARY_600};
    color: ${PRIMARY_600};
  }
`;

const FAILOR_STATUS_RETURNED = "failure";

/**
 * MessagePreview component
 * Displays the consent request message preview with optional FPL checkbox
 */
const MessagePreview = ({ messageTemplate, collectFplDatum, onCollectFplDatumChange, showFplCheckbox }) => {
  const { t } = useTranslation();

  return (
    <MessagePreviewSection>
      <MessagePreviewHeader>
        <MessagePreviewTitle>{t("consent.forms.request.message_preview_title")}</MessagePreviewTitle>
        {showFplCheckbox && (
          <FplCheckboxContainer data-pendo="consent.request.fpl_checkbox">
            <StyledFplCheckbox checked={collectFplDatum} onChange={(e) => onCollectFplDatumChange(e.target.checked)}>
              <FplCheckboxLabel>{t("consent.forms.request.fpl_checkbox_label")}</FplCheckboxLabel>
            </StyledFplCheckbox>
          </FplCheckboxContainer>
        )}
      </MessagePreviewHeader>
      <Divider />
      <MessageText id="consent-request-message-preview-text">{messageTemplate}</MessageText>
      <Divider />
    </MessagePreviewSection>
  );
};

/**
 * PatientContactDetails component
 * Displays patient name and contact methods with checkboxes for selection
 */
const PatientContactDetails = ({ patientDetails, checkedValues, onContactChange, onEditContact }) => {
  const { t } = useTranslation();

  return (
    <ContactDetailsSection id="consent-request-patient-contact-details">
      <PatientNameLabel>{patientDetails?.name || t("consent.forms.patient_fallback")}</PatientNameLabel>
      <StyledCheckboxGroup onChange={onContactChange} value={checkedValues}>
        <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
          <ContactItem>
            <Checkbox disabled={!patientDetails?.email} value={patientDetails?.email}>
              <ContactText $isPlaceholder={!patientDetails?.email}>
                {patientDetails?.email || t("consent.forms.request.add_email_placeholder")}
              </ContactText>
            </Checkbox>
            <EditButton onClick={onEditContact}>
              <BlueEditIcon />
            </EditButton>
          </ContactItem>
          <ContactItem>
            <Checkbox disabled={!patientDetails?.phoneNumber} value={patientDetails?.phoneNumber}>
              <ContactText $isPlaceholder={!patientDetails?.phoneNumber}>
                {patientDetails?.phoneNumber
                  ? formatPhoneNumber(patientDetails.phoneNumber)
                  : t("consent.forms.request.add_phone_placeholder")}
              </ContactText>
            </Checkbox>
            <EditButton onClick={onEditContact}>
              <BlueEditIcon />
            </EditButton>
          </ContactItem>
        </div>
      </StyledCheckboxGroup>
    </ContactDetailsSection>
  );
};

/**
 * RequestButtons component
 * Displays the request and cancel buttons
 */
const RequestButtons = ({ onRequest, onCancel, isLoading, isDisabled }) => {
  const { t } = useTranslation();

  return (
    <ButtonsSection>
      <StyledSecondaryButton
        onClick={onRequest}
        disabled={isDisabled}
        icon={isLoading ? <LoadingOutlined /> : <WhiteSendIcon />}
      >
        {isLoading ? t("consent.forms.loading_states.requesting") : t("consent.forms.request.request_button")}
      </StyledSecondaryButton>

      <StyledOutlinedButton onClick={onCancel} disabled={isLoading}>
        {t("consent.forms.request.cancel_button")}
      </StyledOutlinedButton>
    </ButtonsSection>
  );
};

/**
 * RequestConsentForm component
 * Form for requesting consent from patient via email/phone
 */
const RequestConsentForm = ({ onCancel, onSave, patientDetails, onError }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { patientId, journeyId } = useParams();
  const user = useSelector(selectUser);
  const providerOrgName = useGetSetting(["providerOrgName"]);
  const { track } = useStandardizedTracking();

  const [form] = Form.useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [checkedValues, setCheckedValues] = useState([patientDetails?.email || "", patientDetails?.phoneNumber || ""]);
  const [collectFplDatum, setCollectFplDatum] = useState(false);

  const [isElectronicConsentEnabled, consentText, consentExpirationMonths, isFplEnabled] = useGetSetting([
    "consent.electronic_enabled",
    "consent.text",
    "consent.expiration_months",
    "consent.request_fpl_enabled"
  ]);

  const isRequestConsentAvailable = isElectronicConsentEnabled && consentText && consentExpirationMonths;

  const messageTemplate = t("consent.forms.request.message_template", {
    patientName: patientDetails?.name || "",
    fnName: formatName(user),
    siteName: providerOrgName
  });

  // Step 4: Track box impression when message preview is displayed
  useEffect(() => {
    if (isRequestConsentAvailable && messageTemplate) {
      const enabledMethods = [];
      if (patientDetails?.email) enabledMethods.push("email");
      if (patientDetails?.phoneNumber) enabledMethods.push("sms");

      const messagePreviewTextRedaced = t("consent.forms.request.message_template", {
        patientName: "[REDACTED]",
        fnName: "[REDACTED]",
        siteName: providerOrgName
      });

      // Step 4: Track box impression when message preview is displayed
      track(EventNames.BOX_IMPRESSION, {
        pageKey: "patient_overview",
        featureGroup: FeatureGroups.ECONSENT,
        contentType: ContentTypes.CONSENT,
        contentKey: "consent_message_preview",
        layout: "panel",
        additionalInfo: {
          panel_title: "MESSAGE PREVIEW",
          fpl_checkbox_visible: isFplEnabled,
          fpl_checkbox_enabled: collectFplDatum,
          enabled_send_methods: enabledMethods,
          message_preview_text: messagePreviewTextRedaced
        }
      });
    }
  }, [isRequestConsentAvailable, messageTemplate, collectFplDatum, isFplEnabled]);

  const handleEditContact = () => {
    history.push(`/patient/${patientId}/journey/${journeyId}/edit`, {
      path: location.pathname
    });
  };

  const hasMissingDateOfBirth = !patientDetails?.dob;
  const hasMissingContactInfo = !patientDetails?.email && !patientDetails?.phoneNumber;

  const handleContactChange = (values) => {
    setCheckedValues(values);
  };

  const isRequestDisabled = !checkedValues.length || checkedValues.every((value) => !value);

  const handleRequest = async () => {
    // Move channel detection outside try block so it's accessible in catch
    const requestedEmail = checkedValues.includes(patientDetails?.email);
    const requestedPhone = checkedValues.includes(patientDetails?.phoneNumber);

    try {
      if (hasMissingDateOfBirth || hasMissingContactInfo) {
        return;
      }

      // SECURITY: Send only channel selection, not actual email/phone values
      const channels = [];

      if (requestedEmail) {
        channels.push(CONSENT_REQUEST_CHANNELS.EMAIL);
      }
      if (requestedPhone) {
        channels.push(CONSENT_REQUEST_CHANNELS.PHONE);
      }

      // Step 5: Track Send Request button click before API call
      const enabledMethods = [];
      if (patientDetails?.email) enabledMethods.push("email");
      if (patientDetails?.phoneNumber) enabledMethods.push("sms");

      track(EventNames.BUTTON_CLICK, {
        pageKey: "patient_overview",
        featureGroup: FeatureGroups.ECONSENT,
        contentType: ContentTypes.CONSENT,
        contentKey: "send_consent_request_button",
        additionalInfo: {
          button_text: t("consent.forms.request.request_button"),
          button_id: "btn_send_consent_request",
          fpl_checkbox_enabled: collectFplDatum,
          enabled_send_methods: enabledMethods,
          sms_selected: requestedPhone,
          email_selected: requestedEmail,
          popup_context: "consent_request_popup"
        }
      });

      setIsLoading(true);

      // Remove error if it exists
      if (onError) {
        onError(null);
      }

      const res = await createEConsentRemoteSignatureRequest({
        patientId,
        name: patientDetails?.name,
        channels,
        assignee: user.id,
        collectFplDatum
      });

      const { multiChannelRequests } = res.data;

      const errors = {
        email: requestedEmail && multiChannelRequests?.email?.status === FAILOR_STATUS_RETURNED,
        sms: requestedPhone && multiChannelRequests?.sms?.status === FAILOR_STATUS_RETURNED
      };

      const hasEmailError = errors.email;
      const hasSmsError = errors.sms;
      const hasBothErrors = hasEmailError && hasSmsError;
      const hasAnyError = hasEmailError || hasSmsError;

      // Step 6: Track link sent event after backend confirmation
      if (!hasBothErrors) {
        // At least one channel succeeded
        const sentMethods = [];
        if (!hasEmailError && requestedEmail) sentMethods.push("email");
        if (!hasSmsError && requestedPhone) sentMethods.push("sms");

        track(EventNames.LINK_SENT, {
          pageKey: "patient_overview",
          featureGroup: FeatureGroups.ECONSENT,
          contentType: ContentTypes.CONSENT,
          contentKey: "consent_request_link_sent",
          productSource: "backend", // Backend confirms link was sent
          additionalInfo: {
            enabled_send_methods: sentMethods,
            consent_type: "general_consent",
            link_type: "econsent_request",
            recipient_type: "patient"
          }
        });
      }

      if (hasAnyError && onError) {
        let errorMessage = "";
        if (hasBothErrors) {
          errorMessage = t("consent.forms.request.error_all_channels_failed");
        }
        onError(errorMessage);
      }

      setIsLoading(false);

      if (onSave) {
        // If there is any error related to or not sending sms or not sending email, we want to close the form and still show the error message
        // Build partial success info if one channel succeeded but another failed
        let partialSuccessInfo = null;
        if (!hasBothErrors && hasAnyError) {
          // Partial success - one channel succeeded, one failed
          if (hasEmailError) {
            // Email failed, phone succeeded
            partialSuccessInfo = {
              message: t("consent.forms.request.partial_success_email_failed")
            };
          } else if (hasSmsError) {
            // SMS failed, email succeeded
            partialSuccessInfo = {
              message: t("consent.forms.request.partial_success_sms_failed")
            };
          }
        }

        onSave(partialSuccessInfo);
      }
    } catch (error) {
      // Show specific error message based on selected channels
      let errorMessage;
      if (requestedEmail && requestedPhone) {
        // Both channels selected - show generic error
        errorMessage = t("consent.forms.request.error_all_channels_failed");
      } else if (requestedEmail) {
        // Only email selected
        errorMessage = t("consent.forms.request.error_email_failed");
      } else if (requestedPhone) {
        // Only SMS selected
        errorMessage = t("consent.forms.request.error_sms_failed");
      }

      onError(errorMessage);
      setIsLoading(false);
    }
  };

  const getPatientMissingDetailsMessage = () => {
    if (hasMissingDateOfBirth && hasMissingContactInfo) {
      return t("consent.forms.request.dob_and_contact_error_message");
    } else if (hasMissingDateOfBirth) {
      return t("consent.forms.request.dob_error_message");
    } else if (hasMissingContactInfo) {
      return t("consent.forms.request.contact_error_message");
    }
  };

  return (
    <>
      {isRequestConsentAvailable ? (
        <FormContainer>
          <MessagePreview
            messageTemplate={messageTemplate}
            collectFplDatum={collectFplDatum}
            onCollectFplDatumChange={setCollectFplDatum}
            showFplCheckbox={isFplEnabled}
          />

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              email: !!patientDetails?.email,
              phone: !!patientDetails?.phoneNumber
            }}
            validateTrigger={["onChange", "onBlur"]}
          >
            <RequestSection>
              <PatientContactDetails
                patientDetails={patientDetails}
                checkedValues={checkedValues}
                onContactChange={handleContactChange}
                onEditContact={handleEditContact}
              />

              <RequestButtons
                onRequest={handleRequest}
                onCancel={onCancel}
                isLoading={isLoading}
                isDisabled={isLoading || hasMissingDateOfBirth || hasMissingContactInfo || isRequestDisabled}
              />
            </RequestSection>
          </Form>

          {(hasMissingContactInfo || hasMissingDateOfBirth) && (
            <RequestConsentLinkErrorAlert message={getPatientMissingDetailsMessage()} />
          )}
        </FormContainer>
      ) : (
        <ElectronicConsentAlert />
      )}
    </>
  );
};

export default RequestConsentForm;
